// SPS-5005 "Your Advisor" (Meetings Hub) — DEV fixtures.
//
// These mirror the planned II Meetings Hub DTOs so the page renders end-to-end
// without a live backend. MeetingsService falls back to these whenever the API
// call fails or returns an empty payload.
//
// The sample content matches the SPS-5005 Figma ("Your Advisor" / advisor Daphne
// Kim). Display-only overrides (dateLabel, dayNum, monthShort, recordingLength,
// relativeLabel, timeMeta, keyMoments) let the fixtures reproduce the mockup's
// exact copy; the component falls back to deriving these from `date` for real
// API payloads that don't carry them.

export type MeetingType = 'strategy' | 'review' | 'planning' | 'check-in' | 'onboarding'

export interface MeetingAttendee {
  name: string
  role?: string
  avatarUrl?: string | null
  initials?: string
  color?: string
}

export interface MeetingActionItem {
  text: string
  due?: string | null
  status: 'open' | 'in_progress' | 'done'
  owner?: string | null
  // Dashboard sync state shown as a pill in the expanded "Actions from this call" list.
  badge?: 'in_dashboard' | 'done_in_dashboard' | null
  // Indie-suggested action rendered as the dashed "not yet added" card.
  suggested?: boolean
}

export interface MeetingTranscriptLine {
  time: string
  speaker: string
  text: string
}

export interface MeetingAgendaItem {
  text: string
  done?: boolean
}

export interface MeetingKeyMoment {
  time: string
  title: string
  quote: string
}

export interface AdvisorRelationshipStat {
  label: string
  value: string
  sub?: string | null
}

export interface MeetingAdvisor {
  id: string
  name: string
  title: string
  avatarUrl?: string | null
  email?: string | null
  bio?: string | null
  initials?: string
  // e.g. "Working together for 2.4 years" (green-dot line under the title).
  relationshipLabel?: string | null
  relationshipStats: AdvisorRelationshipStat[]
}

export interface Meeting {
  id: string
  title: string
  type: MeetingType
  typeLabel?: string | null
  date: string // ISO
  // Display overrides (used verbatim when present; otherwise derived from `date`).
  dateLabel?: string | null // "Wednesday, May 14" or "May 6, 2026"
  dayNum?: string | null // "14" — big day number in the Upcoming list
  monthShort?: string | null // "MAY"
  duration?: string | null // e.g. "45 min"
  recordingLength?: string | null // e.g. "48:22" (thumbnail + player time)
  timeMeta?: string | null // "10:00 AM CT · 45 min · Zoom"
  relativeLabel?: string | null // "In 3 days" pill
  scope: 'upcoming' | 'past' | 'next'
  starred?: boolean
  isNew?: boolean
  topics?: string[]
  attendees: MeetingAttendee[]
  attendeeSummary?: string | null // "Daphne Kim · Rose D. · 48 min"
  briefTitle?: string | null // "Indie has a pre-call brief ready"
  brief?: string | null // short pre-call blurb (used by Next call card)
  joinUrl?: string | null
  agendaItems?: MeetingAgendaItem[]
  summary?: string | null
  keyTakeaways?: string[]
  keyMoments?: MeetingKeyMoment[]
  notes?: string | null
  actionItems?: MeetingActionItem[]
  transcript?: MeetingTranscriptLine[]
  hasRecording?: boolean
  // Direct playable recording source (signed mp4). Populated by the real-data fallback.
  videoUrl?: string | null
  // grain.com share link for this recording (opens the native Grain viewer in a new tab).
  // Populated by the real-data fallback alongside videoUrl.
  grainUrl?: string | null
  thumbnailUrl?: string | null
}

// Static design copy for the Call history header + "Load earlier" footer. These are
// mockup totals (14 interactions, 10 behind the fold); starred/new counts are derived
// from the loaded rows at render time.
export const MEETINGS_HISTORY_META = {
  totalInteractions: 14,
  sinceLabel: 'March 2024',
  earlierCount: 10,
}

// The topic taxonomy shown as filter chips (design order). "All" is prepended in the UI.
export const MEETINGS_TOPICS = [
  'OTB Review',
  'Buying',
  'Planning',
  'Performance',
  'Trunk Show',
  'Markdown',
]

const ADVISOR: MeetingAdvisor = {
  id: 'adv-001',
  name: 'Daphne Kim',
  title: 'Retail Planning Expert · Management One',
  avatarUrl: null,
  email: 'daphne.kim@management-one.com',
  initials: 'DK',
  relationshipLabel: 'Working together for 2.4 years',
  relationshipStats: [
    { label: 'Months on plan', value: '21' },
    { label: 'Markdown', value: '−4.1%' },
    { label: 'Inv. guided', value: '$2.2M' },
  ],
}

const NEXT_CALL: Meeting = {
  id: 'mtg-next',
  title: 'Monthly strategy call',
  type: 'strategy',
  typeLabel: 'Call',
  date: '2026-05-14T15:00:00.000Z',
  dateLabel: 'Wednesday, May 14',
  timeMeta: '10:00 AM CT · 45 min · Zoom',
  relativeLabel: 'In 3 days',
  duration: '45 min',
  scope: 'next',
  briefTitle: 'Indie has a pre-call brief ready',
  brief: 'OTB status, top performers, 3 open actions from May 6',
  joinUrl: 'https://zoom.us/j/orbit-monthly',
  attendees: [
    { name: 'Daphne Kim', role: 'Advisor', initials: 'DK', color: '#7F56D9' },
    { name: 'You', role: 'Owner', initials: 'R', color: '#6366F1' },
  ],
  agendaItems: [
    { text: 'Review May OTB and Dressy Tops opportunity', done: false },
    { text: 'Jewelry + Trunk Show planning for June', done: false },
    { text: 'Q3 inflow calendar review', done: false },
  ],
}

const UPCOMING: Meeting[] = [
  {
    id: 'mtg-u1',
    title: 'Monthly strategy call',
    type: 'strategy',
    typeLabel: 'Call',
    date: '2026-05-14T15:00:00.000Z',
    dayNum: '14',
    monthShort: 'MAY',
    timeMeta: '10:00 AM CT · 45 min',
    relativeLabel: 'In 3 days',
    duration: '45 min',
    scope: 'upcoming',
    attendees: [{ name: 'Daphne Kim', role: 'Advisor', initials: 'DK', color: '#7F56D9' }],
  },
  {
    id: 'mtg-u2',
    title: 'Q2 mid-point review',
    type: 'review',
    typeLabel: 'Call',
    date: '2026-06-02T15:00:00.000Z',
    dayNum: '2',
    monthShort: 'JUN',
    timeMeta: '10:00 AM CT · 60 min',
    duration: '60 min',
    scope: 'upcoming',
    attendees: [{ name: 'Daphne Kim', role: 'Advisor', initials: 'DK', color: '#7F56D9' }],
  },
  {
    id: 'mtg-u3',
    title: 'Monthly strategy call',
    type: 'strategy',
    typeLabel: 'Call',
    date: '2026-07-07T15:00:00.000Z',
    dayNum: '7',
    monthShort: 'JUL',
    timeMeta: '10:00 AM CT · 45 min',
    duration: '45 min',
    scope: 'upcoming',
    attendees: [{ name: 'Daphne Kim', role: 'Advisor', initials: 'DK', color: '#7F56D9' }],
  },
]

const PAST: Meeting[] = [
  {
    id: 'mtg-p1',
    title: 'Monthly strategy call',
    type: 'strategy',
    typeLabel: 'Call',
    date: '2026-05-06T15:00:00.000Z',
    dateLabel: 'May 6, 2026',
    recordingLength: '48:22',
    duration: '48 min',
    hasRecording: true,
    scope: 'past',
    starred: true,
    isNew: true,
    topics: ['OTB Review', 'Buying'],
    attendees: [
      { name: 'Daphne Kim', initials: 'DK', color: '#7F56D9' },
      { name: 'Rose D.', initials: 'R', color: '#6366F1' },
    ],
    attendeeSummary: 'Daphne Kim · Rose D. · 48 min',
    summary:
      'Covered May OTB status, Dressy Tops sell-through (78%), and the upcoming Tanglewood Trunk Show. Daphne flagged Jewelry as a strong June opportunity. Three action items were surfaced and pushed to the dashboard.',
    keyMoments: [
      {
        time: '4:12',
        title: 'Dressy Tops sell-through flagged',
        quote: '“78% already — that’s your signal to chase this hard before month end”',
      },
      {
        time: '16:48',
        title: 'OTB allocation decision',
        quote: '“Put at least $15K of remaining OTB into Dressy Tops before end of week”',
      },
      {
        time: '28:33',
        title: 'Jewelry opportunity raised',
        quote: '“There’s a pattern in the Jewelry category that needs a plan for June”',
      },
      {
        time: '41:05',
        title: 'Trunk Show prep outlined',
        quote: '“Use the Tanglewood show to test the Jewelry assortment before committing”',
      },
    ],
    notes:
      'Owner is leaning into Dressy Tops for the rest of May. Daphne to pull the Jewelry category trend before the June planning call. Confirm Tanglewood Trunk Show logistics with the vendor rep.',
    actionItems: [
      { text: 'OTB review sent to Daphne', status: 'done', badge: 'done_in_dashboard' },
      { text: 'Chase Dressy Tops OTB — allocate $15K', status: 'open', badge: 'in_dashboard' },
      { text: 'Book Jewelry strategy call for June', status: 'open', badge: 'in_dashboard' },
      { text: 'Follow up on Trunk Show inventory pre-order', status: 'open', suggested: true },
    ],
    transcript: [
      { time: '00:00', speaker: 'Daphne Kim', text: 'Thanks for making time. Let’s start with where May OTB stands.' },
      { time: '04:12', speaker: 'Daphne Kim', text: 'Dressy Tops are at 78% sell-through already — that’s your signal to chase this hard before month end.' },
      { time: '16:48', speaker: 'Daphne Kim', text: 'Put at least $15K of remaining OTB into Dressy Tops before end of week.' },
      { time: '28:33', speaker: 'Rose D.', text: 'There’s a pattern in the Jewelry category that needs a plan for June.' },
      { time: '41:05', speaker: 'Daphne Kim', text: 'Use the Tanglewood show to test the Jewelry assortment before committing.' },
    ],
  },
  {
    id: 'mtg-p2',
    title: 'Q2 planning kickoff',
    type: 'planning',
    typeLabel: 'Call',
    date: '2026-04-08T15:00:00.000Z',
    dateLabel: 'Apr 8, 2026',
    recordingLength: '62:07',
    duration: '62 min',
    hasRecording: true,
    scope: 'past',
    starred: true,
    isNew: true,
    topics: ['Planning'],
    attendees: [
      { name: 'Daphne Kim', initials: 'DK', color: '#7F56D9' },
      { name: 'Rose D.', initials: 'R', color: '#6366F1' },
    ],
    attendeeSummary: 'Daphne Kim · Rose D. · 62 min',
    summary:
      'Set the Q2 plan: receipt flow by class, a markdown cadence for spring carryover, and the open-to-buy guardrails for May and June.',
    keyMoments: [
      { time: '6:20', title: 'Q2 receipt flow mapped', quote: '“Front-load denim and accessories through April”' },
      { time: '24:10', title: 'Spring carryover cadence', quote: '“Step markdowns 20% now, 40% after two weeks”' },
    ],
    notes: 'Lock receipt timing with the two key vendors. Revisit OTB guardrails after the first May sell-through read.',
    actionItems: [
      { text: 'Publish Q2 receipt calendar', status: 'done', badge: 'done_in_dashboard' },
      { text: 'Set spring carryover markdown cadence', status: 'open', badge: 'in_dashboard' },
    ],
    transcript: [
      { time: '00:00', speaker: 'Daphne Kim', text: 'Let’s frame the Q2 plan end to end.' },
      { time: '06:20', speaker: 'Daphne Kim', text: 'Front-load denim and accessories through April.' },
      { time: '24:10', speaker: 'Rose D.', text: 'Agreed on stepping the carryover markdowns.' },
    ],
  },
  {
    id: 'mtg-p3',
    title: 'Mid-month check-in',
    type: 'check-in',
    typeLabel: 'Call',
    date: '2026-03-11T15:00:00.000Z',
    dateLabel: 'Mar 11, 2026',
    recordingLength: null,
    duration: '22 min',
    hasRecording: false,
    scope: 'past',
    starred: false,
    isNew: false,
    topics: ['Performance'],
    attendees: [
      { name: 'Daphne Kim', initials: 'DK', color: '#7F56D9' },
      { name: 'Rose D.', initials: 'R', color: '#6366F1' },
    ],
    attendeeSummary: 'Daphne Kim · Rose D. · 22 min',
    summary:
      'Quick pulse on month-to-date sales and sell-through. Nothing off-plan; agreed to hold the current receipt flow and reconnect at month end.',
    keyMoments: [],
    notes: 'No recording for this call. Owner to flag any softness in the back half of the month.',
    actionItems: [{ text: 'Recheck sell-through at month end', status: 'open' }],
    transcript: [],
  },
  {
    id: 'mtg-p4',
    title: 'Q1 annual review',
    type: 'review',
    typeLabel: 'Call',
    date: '2026-03-04T15:00:00.000Z',
    dateLabel: 'Mar 4, 2026',
    recordingLength: '78:14',
    duration: '78 min',
    hasRecording: true,
    scope: 'past',
    starred: true,
    isNew: false,
    topics: ['Performance'],
    attendees: [
      { name: 'Daphne Kim', initials: 'DK', color: '#7F56D9' },
      { name: 'Rose D.', initials: 'R', color: '#6366F1' },
      { name: 'Marcus T.', initials: 'M', color: '#17B26A' },
    ],
    attendeeSummary: 'Daphne Kim · +2 others · 78 min',
    summary:
      'Full-year review: margin, turn, and markdown trend versus plan and versus peers. Set the three priorities that shape the Q2 plan.',
    keyMoments: [
      { time: '12:40', title: 'Margin vs peers', quote: '“You’re running 4 points under peer markdowns — protect it”' },
      { time: '47:02', title: 'Turn improvement path', quote: '“Tighten receipts on the slow classes to lift turn”' },
    ],
    notes: 'Share the peer benchmark deck. Owner to pick the two classes to tighten first.',
    actionItems: [
      { text: 'Send peer benchmark deck', status: 'done', badge: 'done_in_dashboard' },
      { text: 'Pick two slow classes to tighten', status: 'open', badge: 'in_dashboard' },
    ],
    transcript: [
      { time: '00:00', speaker: 'Daphne Kim', text: 'Let’s walk the full year against plan and peers.' },
      { time: '12:40', speaker: 'Daphne Kim', text: 'You’re running four points under peer markdowns — protect it.' },
      { time: '47:02', speaker: 'Marcus T.', text: 'Where do we find the turn improvement?' },
    ],
  },
]

export const meetingsFixtures = {
  advisor: ADVISOR,
  nextCall: NEXT_CALL,
  upcoming: UPCOMING,
  past: PAST,
}

// The optional clientCode arg is accepted (and ignored) so these synthetic fixtures
// share a call signature with the live service's per-client fetches.
export function getFixtureAdvisor(_clientCode?: string): MeetingAdvisor {
  return ADVISOR
}

export function getFixtureMeetings(scope: 'upcoming' | 'past', _clientCode?: string): Meeting[] {
  if (scope === 'upcoming') {
    return [NEXT_CALL, ...UPCOMING]
  }
  return PAST
}

export function getFixtureMeeting(id: string, _clientCode?: string): Meeting | null {
  const all = [NEXT_CALL, ...UPCOMING, ...PAST]
  return all.find((meeting) => meeting.id === id) || null
}

export function getFixtureTranscript(id: string, _clientCode?: string): MeetingTranscriptLine[] {
  const meeting = getFixtureMeeting(id)
  return meeting?.transcript || []
}
